// M10-15780. Shown when the OMS blocks PO generation because products in the corp product master
// are missing SAP configuration that PO generation depends on. Which columns are required varies
// per company — sales_org always, plus doc_type / dist_channel / division_sap when that company's
// PO CSV config or PO-submit payload uses them — so the modal reports the checked set in the
// header and the per-row gaps in the table rather than hardcoding a single field. The overlay
// mirrors the JSON-preview modal in PoBulk.tsx so the PO screens stay visually consistent.
import { Button } from "src/components";

export type PoConfigField = "sales_org" | "doc_type" | "dist_channel" | "division_sap";

// Display labels, mirroring PO_CONFIG_FIELD_LABELS in backend-oms-svc/src/api/po/po.utility.ts.
// An unrecognised field falls back to its raw name rather than rendering blank, so a backend that
// starts checking a new column still produces a readable chip before the UI is updated.
const FIELD_LABELS: Record<string, string> = {
    sales_org: "Sales Org",
    doc_type: "Doc Type",
    dist_channel: "Distribution Channel",
    division_sap: "SAP Division",
};

const labelFor = (field: string) => FIELD_LABELS[field] ?? field;

// The table is a preview, the CSV is the work list. Rendering the whole response would make the
// modal unusable on a large gap list, but the export must still carry every row we were given —
// keeping these two separate is the entire point of this constant.
const DISPLAY_LIMIT = 200;

export interface PoConfigGapProduct {
    item_divisiondtl_id: number;
    company_productcode: number;
    item_code: string;
    item_description: string;
    company_divisioncode: number;
    division_name: string;
    division_shortcode: string;
    missing_fields: PoConfigField[];
    values?: Partial<Record<PoConfigField, string | null>>;
}

interface PoConfigValidationModalProps {
    show: boolean;
    total: number;
    products: PoConfigGapProduct[];
    message?: string;
    companyName?: string;
    moduleDesc?: string | null;
    moduleCode?: number;
    checkedFields?: PoConfigField[];
    onClose: () => void;
}

const PoConfigValidationModal = ({
    show,
    total,
    products,
    message,
    companyName,
    moduleDesc,
    moduleCode,
    checkedFields,
    onClose,
}: PoConfigValidationModalProps) => {
    if (!show) return null;

    const visibleProducts = products.slice(0, DISPLAY_LIMIT);

    // Three counts can differ: what is on screen, what the CSV holds, and what actually exists.
    // Say all of it rather than letting them silently disagree.
    const isTruncated = total > visibleProducts.length;

    // Module and company belong in the header, not in a column: the whole check is scoped to one
    // company + module, so repeating them on every row is noise.
    const context = [
        companyName ? `Company: ${companyName}` : null,
        moduleDesc || moduleCode ? `Module: ${moduleDesc || "-"}${moduleCode ? ` (${moduleCode})` : ""}` : null,
        checkedFields?.length ? `Checked: ${checkedFields.map(labelFor).join(", ")}` : null,
    ].filter(Boolean);

    // Product Master details has no URL-param support — details-section.component.tsx reads
    // `detailsId` from sessionStorage and redirects to the list when it is absent. Every other
    // call site has to search for the product first to get its company_productcode; this response
    // already carries it, so the new tab opens synchronously inside the click handler and no
    // popup blocker sees it. All rows are active_status='Y' by construction of the check.
    const openProductMaster = (item: PoConfigGapProduct) => {
        window.sessionStorage.setItem("detailsId", String(item.company_productcode));
        window.sessionStorage.setItem("product_activeStatus", "Y");
        window.open("/masters/products/details", "_blank");
    };

    const downloadCsv = () => {
        const header = ["Item Code", "Product", "Division Code", "Division", "Missing Field(s)"];
        // Wrap every cell and double up embedded quotes — product and division names contain commas.
        const escape = (value: unknown) => `"${String(value ?? "").replace(/"/g, '""')}"`;
        // Deliberately `products`, not `visibleProducts` — the export is the reason the full list
        // is fetched at all; narrowing it to the rendered slice is the bug this replaced.
        const rows = products.map((item) =>
            [
                item.item_code,
                item.item_description,
                item.company_divisioncode,
                item.division_name,
                (item.missing_fields ?? []).map(labelFor).join(" / "),
            ]
                .map(escape)
                .join(",")
        );
        const blob = new Blob([[header.map(escape).join(","), ...rows].join("\n")], {
            type: "text/csv;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `po-config-gaps${moduleCode ? `-module-${moduleCode}` : ""}-${Date.now()}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded shadow-md max-w-4xl w-full max-h-[80vh] flex flex-col">
                <h2 className="text-lg font-semibold text-red-600">PO generation blocked</h2>
                <p className="mt-2 text-sm text-gray-700">
                    {message ||
                        `${total} product(s) in the product master are missing required configuration. Update the Product Master and try again.`}
                </p>
                {context.length > 0 && (
                    <p className="mt-1 text-xs text-gray-500">{context.join(" · ")}</p>
                )}
                {isTruncated && (
                    <p className="mt-1 text-xs text-gray-500">
                        Showing the first {visibleProducts.length} of {total}. The CSV download contains{" "}
                        {products.length >= total ? `all ${total}` : `the first ${products.length}`}.
                    </p>
                )}

                <div className="flex-grow mt-4 overflow-auto border rounded">
                    <table className="w-full text-sm text-left">
                        <thead className="sticky top-0 bg-gray-100 text-gray-700">
                            <tr>
                                <th className="px-3 py-2 font-medium">Item Code</th>
                                <th className="px-3 py-2 font-medium">Product</th>
                                <th className="px-3 py-2 font-medium">Division</th>
                                <th className="px-3 py-2 font-medium">Missing Field(s)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleProducts.map((item) => (
                                <tr
                                    key={`${item.item_divisiondtl_id}`}
                                    className="border-t border-gray-200 align-top"
                                >
                                    <td className="px-3 py-2 whitespace-nowrap">
                                        <span
                                            className="text-violet-600 underline cursor-pointer"
                                            title="Open this product in Product Master"
                                            onClick={() => openProductMaster(item)}
                                        >
                                            {item.item_code}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2">{item.item_description || "-"}</td>
                                    <td className="px-3 py-2">
                                        {item.division_name || "-"}
                                        <span className="text-gray-400"> ({item.company_divisioncode})</span>
                                    </td>
                                    <td className="px-3 py-2">
                                        <span className="flex flex-wrap gap-1">
                                            {(item.missing_fields ?? []).map((field) => (
                                                <span
                                                    key={field}
                                                    className="rounded bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 text-xs whitespace-nowrap"
                                                >
                                                    {labelFor(field)}
                                                </span>
                                            ))}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="flex justify-end gap-2 mt-4">
                    <Button onClick={onClose}>Close</Button>
                    <Button
                        className="bg-violet-500 text-white hover:bg-violet-600"
                        onClick={downloadCsv}
                    >
                        Download CSV
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default PoConfigValidationModal;
